import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Checkbox, IconClickable, Input, Select, Text, View } from 'src/components/common';
import { IRootState } from 'src/redux/rootReducer';
import { getDailyTimesheetAuditAsync } from 'src/redux/timesheet/actions';
import { AuditHistory, DailyTimesheet } from 'src/redux/timesheet/types';
import { isEmpty } from 'src/validations';
import { dailyTableHeader, INITIAL_JOB, isDisabledSaveDailyTimesheet, isValidHours } from '../../helpers';
import '../styles.scss';

const createActiveOptions = options => {
  if (!options) return [];

  return options
    ?.map(item => ({
      value: item.id,
      label: `${item.code} - ${item.description}`,
      isdisabled: item.status !== 'active',
    }))
    ?.filter(item => !item.isdisabled);
};

const DataTable: React.FC<Props> = ({
  dailyData,
  weeklyTableData,
  errors,
  tasks,
  jobs,
  phases,
  loading,
  isShowError,
  isDirty,
  onCancel,
  onValueChange,
  onGetAudits,
}) => {
  const [auditHistory, setAuditHistory] = useState<AuditHistory>({});
  const activeJobOptions = createActiveOptions(jobs);
  const activePhaseOptions = createActiveOptions(phases);

  useEffect(() => {
    if (dailyData.auditHistoryUri) {
      onGetAudits({ uri: dailyData.auditHistoryUri, callback: handleGetAudits });
    }

    createJobOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyData?.id]);
  const handleGetAudits = auditData => {
    setAuditHistory(auditData);
  };

  const createOptions = (optionId, activeOptions, options) => {
    const existedOption = activeOptions.find(option => option.value === optionId);

    if (existedOption) return activeOptions;

    const foundOption = options.find(option => option.id === optionId);

    if (!foundOption) return activeOptions;

    return [
      ...activeOptions,
      {
        value: foundOption.id,
        label: `${foundOption.code} - ${foundOption.description}`,
        isdisabled: foundOption.status !== 'active',
      },
    ];
  };

  const createJobOptions = () => {
    dailyData.jobs = dailyData.jobs.map(record => {
      if (record?.job) {
        record.jobOptions = createOptions(record.job, activeJobOptions, jobs);
      }

      if (record?.phaseCode) {
        record.phaseOptions = createOptions(record.phaseCode, activePhaseOptions, phases);
      }

      return record;
    });
  };

  const [taskOptions, setTaskOptions] = useState([]);
  const [touched, setTouched] = useState({});
  const rowField = ['job', 'task', 'lots', 'hours', 'phaseCode', 'extra', 'action'];

  const header = useMemo(
    () =>
      dailyTableHeader.map(list => (
        <TableRow>
          {list.map((item, index) => (
            <TableCell colSpan={item.colSpan} rowSpan={item.rowSpan} key={`dailyTableHeader${index}`}>
              {item.title}
              {item.subTitle && (
                <span className="cmp-count-table__header--light">
                  <br />
                  {item.subTitle}
                </span>
              )}
            </TableCell>
          ))}
        </TableRow>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dailyTableHeader],
  );
  // ====================== Handlers ======================

  const handleRowInputValueChange = (e, index, isCheckbox = false) => {
    const name = e.target.name;
    const value = isCheckbox ? e.target.checked : e.target.value;
    if (name === 'hours') {
      Number(value) <= 24 && handleRowValueChange(name, value.replace(/(\.\d\d)(\d+)/, '$1'), index);
    } else {
      handleRowValueChange(name, value, index);
    }
  };

  const handleRowValueChange = (name, value, index) => {
    const jobs = dailyData.jobs.map((item, i) => {
      if (i === index) {
        return { ...item, [name]: value };
      }
      return item;
    });
    onValueChange('jobs', jobs);
  };

  const handleAddRow = () => {
    const jobs = dailyData.jobs.concat(INITIAL_JOB);
    onValueChange('jobs', jobs);
  };
  const handleRemoveRow = (index: number) => {
    let jobs = dailyData.jobs;
    if (dailyData.jobs.length === 1) {
      jobs = [INITIAL_JOB];
    } else {
      jobs = jobs.filter((e, i) => i !== index);
    }
    onValueChange('jobs', jobs);
  };
  const handelSetTouched = (name: string) => {
    let current = touched;
    current[name] = true;
    setTouched(touched);
  };
  // ========================= Render views =========================

  const onControlClick = (index, newValue) => {
    const e = { target: { name: 'hours', value: `${newValue}` } };
    handleRowInputValueChange(e, index);
  };

  const disableDatalist = () => {
    setTaskOptions([]);
  };

  const currentTaskOptions = tasks?.map(item => item.name);
  const enableDatalist = useCallback(() => {
    setTaskOptions(currentTaskOptions);
  }, [currentTaskOptions]);
  const body = dailyData.jobs.map((record, index) => (
    <TableRow key={`dailyDataJob${index}`}>
      <TableCell>
        <Select
          isSearchable={false}
          creatable={false}
          key={`${dailyData.date}-job-${index}`}
          label={''}
          value={record.job}
          options={record.jobOptions || activeJobOptions}
          placeholder="Select"
          name="job"
          errorMessage={touched[`job${index}`] && !record.job ? ' ' : null}
          onBlur={() => {
            handelSetTouched(`job${index}`);
          }}
          onChange={(name, value) => handleRowValueChange(name, value, index)}
        />
      </TableCell>
      <TableCell>
        <Input
          key={`${dailyData.date}-lots-${index}`}
          datalist={taskOptions}
          datalistName="TaskOptions"
          dataToolTip={typeof record.task === 'undefined' ? undefined : `${record.task}`}
          placeholder="Task"
          name="task"
          value={`${record.task}`}
          errorMessage={touched[`task${index}`] && (!record.task || isEmpty(record.task)) ? ' ' : null}
          onBlur={() => {
            handelSetTouched(`task${index}`);
          }}
          onFocus={disableDatalist}
          onKeyDown={enableDatalist}
          onChange={e => handleRowInputValueChange(e, index)}
        />
      </TableCell>
      <TableCell style={{ maxWidth: 160 }}>
        <Input
          key={`${dailyData.date}-lots-${index}`}
          placeholder="Lot#s"
          name="lots"
          value={`${record.lots}`}
          onChange={e => handleRowInputValueChange(e, index)}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          key={`${dailyData.date}-hours-${index}`}
          placeholder="Hours"
          name="hours"
          min={0.25}
          max={24}
          step={0.25}
          value={`${record.hours}`}
          errorMessage={touched[`hours${index}`] && !isValidHours(record.hours) ? ' ' : null}
          onBlur={() => {
            handelSetTouched(`hours${index}`);
          }}
          onControlClick={newValue => onControlClick(index, newValue)}
          onChange={e => handleRowInputValueChange(e, index)}
        />
      </TableCell>
      <TableCell>
        <Select
          isSearchable={false}
          creatable={false}
          key={`${dailyData.date}-phaseCode-${index}`}
          label={''}
          value={record.phaseCode}
          options={record.phaseOptions || activePhaseOptions}
          placeholder="Select"
          name={'phaseCode'}
          errorMessage={touched[`phaseCode${index}`] && !record.phaseCode ? ' ' : null}
          onBlur={() => {
            handelSetTouched(`phaseCode${index}`);
          }}
          onChange={(name, value) => handleRowValueChange(name, value, index)}
        />
      </TableCell>
      <TableCell align="center">
        <Checkbox.Item
          key={`${dailyData.date}-extra-${index}`}
          className="mb-16 mt-16 ml-16"
          name="isExtra"
          checked={record.isExtra}
          onChange={e => handleRowInputValueChange(e, index, true)}
        />
      </TableCell>
      <TableCell align="center">
        {dailyData.jobs.length === 1 ? (
          <IconClickable
            className="cmp-count-table__action--disabled"
            key={`${dailyData.date}-action-${index}`}
            name="ic_trash"
          />
        ) : (
          <IconClickable
            className="cmp-count-table__action"
            key={`${dailyData.date}-action-${index}`}
            name="ic_trash"
            onClick={() => handleRemoveRow(index)}
          />
        )}
      </TableCell>
    </TableRow>
  ));

  let totalHours: number = 0;
  dailyData.jobs.forEach(element => {
    totalHours += Number(element.hours);
  });

  const { isNotWorking } = dailyData;
  const hasError = !!errors?.jobs && isShowError;
  const isUpdate = !!weeklyTableData.id;

  return (
    <View className="ctn-timesheet-daily__table">
      {isNotWorking ? (
        <Text size={24} className="ctn-timesheet-daily__table--not-worked">
          This employee didn’t work on this day.
        </Text>
      ) : (
        <View className="ctn-timesheet-daily__table--box mb-24">
          <Table>
            <TableHead>{header}</TableHead>
            <TableBody>{body}</TableBody>
            <TableRow>
              <TableCell colSpan={rowField.length}>
                <Button variant="link-primary" label="+ Add another Row" onClick={handleAddRow} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell />
              <TableCell>TOTAL</TableCell>
              <TableCell>
                <b>{totalHours}</b>
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          </Table>
          {hasError && <p className="ctn-timesheet-daily__error mt-8">{errors?.jobs}</p>}
        </View>
      )}
      {isUpdate && (
        <View className="mb-24">
          <Accordion className="ctn-timesheet-daily__audit" square>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Text className="ctn-timesheet-daily__audit--title">Audit History</Text>
            </AccordionSummary>
            <AccordionDetails>
              <ul className="ctn-timesheet-daily__audit--detail">
                {!auditHistory.creator && !auditHistory.updater && <li key="auditNo">No audit history.</li>}
                {!!auditHistory.updater && (
                  <li key="auditUpdate">
                    Updated by{' '}
                    <span className="ctn-timesheet-daily__audit--title">{`${auditHistory.updater.firstName} ${auditHistory.updater.lastName}`}</span>{' '}
                    at{' '}
                    <span className="ctn-timesheet-daily__audit--title">
                      {moment(auditHistory.updater.updatedDate).format('HH:mm, MM/DD/yyyy')}
                    </span>
                  </li>
                )}

                {!!auditHistory.creator && (
                  <li key="auditCreate">
                    Created by{' '}
                    <span className="ctn-timesheet-daily__audit--title">{`${auditHistory.creator.firstName} ${auditHistory.creator.lastName}`}</span>{' '}
                    at{' '}
                    <span className="ctn-timesheet-daily__audit--title">
                      {moment(auditHistory.creator.createdDate).format('HH:mm, MM/DD/yyyy')}
                    </span>
                  </li>
                )}
              </ul>
            </AccordionDetails>
          </Accordion>
        </View>
      )}
      <View isRow justify="flex-end" className="ctn-timesheet-daily__table--action">
        <Button type="button" label="CANCEL" variant="outline" className="mr-16" onClick={onCancel} />
        <Button
          type="submit"
          label="SAVE"
          isLoading={loading}
          disabled={(!dailyData.isNotWorking && isDisabledSaveDailyTimesheet(dailyData.jobs)) || !isDirty}
        />
      </View>
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps & {
    dailyData: DailyTimesheet;
    errors: any;
    onCancel: (...args: any[]) => void;
    onValueChange: (...args: any[]) => void;
    isShowError: boolean;
    isDirty: boolean;
  };

const mapStateToProps = (state: IRootState) => ({
  user: state.auth.authUser,
  weeklyTableData: state.timesheet.weeklyTableData,

  jobs: state.jobCodes.jobs,
  phases: state.phaseCodes.phases,
  tasks: state.task.tasks,
  loading: state.timesheet.isLoading,
});

const mapDispatchToProps = {
  onGetAudits: getDailyTimesheetAuditAsync.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DataTable);
